import React from 'react';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { ENV } from './env';
import { loadFromCookies, loadFromLocalStorage, removeFromCookies, saveToCookies } from './utils';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'src/redux/hooks';
import { removeUserInfo } from 'src/redux/features/login-slice';

import PATH from 'src/constants/pathes';
import { STORAGE_KEYS } from 'src/constants/constants';
import i18n from 'src/utils/translate/config';

export const BASE_URL = ENV.IS_PRODUCTION
  ? 'https://www.royalhealthcare.asia/prod2/api/'
  : 'https://royalhealthcare.asia/dev2/api/';
// : 'http://localhost:8089/'; // Nam: don't commit this line




axios.defaults.timeout = 120000; // 120s
axios.defaults.baseURL = BASE_URL;
axios.defaults.withCredentials = true; // Enable sending cookies (session) with requests


// Handle token refresh & queue pending requests
type failedQueueTypes = {
  reject: (error: Error) => void;
  resolve: (value?: unknown) => void;
};

export type AxiosConfigWithInterceptedSetting = AxiosRequestConfig & {
  _retry?: boolean;
  _isSkipCommonError?: boolean;
  _isSkipAdditionlAuthError?: boolean;
  url?: string;
};

// token update process handling
// See https://gist.github.com/Godofbrowser/bf118322301af3fc334437c683887c5f
let isTokenRefreshing = false;
let failedQueue: failedQueueTypes[] = [];

const processQueue = (error: Error | null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

export const HttpInterceptor: React.FC = props => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const navigateToLoginWithCurrentUrl = () => {
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (window.location.pathname === PATH.LOGIN_PAGE) return;
    navigate(PATH.LOGIN_PAGE, {
      state: { from: currentPath },
      replace: true,
    });
  };

  axios.interceptors.request.use(
    config => {
      const accessToken = loadFromCookies('access_token', true);
      const currentLanguage = i18n.language;
      if (config.headers) {
        config.headers['Accept-Language'] = currentLanguage || 'vi';

        if (accessToken) config.headers['Authorization'] = `Bearer ${accessToken}`;
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (response: AxiosResponse<any>) => {
      return response;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (error: AxiosError<any>) => {
      const originalRequest = error.config as AxiosConfigWithInterceptedSetting;
      let status = error.response ? error.response.status : error.code;
      status = status === 'ECONNABORTED' ? 'timeout' : status;
      const errStatus: string = (status ? status : '').toString();

      if (errStatus === '401') {
        const accessToken = loadFromCookies('access_token', true);

        if (!accessToken) {
          navigateToLoginWithCurrentUrl();
          return;
        }

        if (originalRequest._retry) {
          return Promise.reject(error);
        }

        if (isTokenRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve,
              reject,
            });
          })
            .then(() => {
              return axios(originalRequest);
            })
            .catch(() => {
              return Promise.reject(error);
            });
        }

        originalRequest._retry = true;
        isTokenRefreshing = true;

        return new Promise((resolve, reject) => {
          const userId = loadFromLocalStorage(STORAGE_KEYS.USER_ID);

          axios
            .post(`${BASE_URL}checktoken/${userId}`, { token: accessToken })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then(({ data }: any) => {
              if (data.status === 200) {
                saveToCookies({ name: 'access_token', value: data.token, maxAge: 3600 });
                setTimeout(() => {
                  processQueue(null);
                  resolve(axios(originalRequest));
                }, 100);
              } else {
                removeFromCookies('access_token');
                navigateToLoginWithCurrentUrl();
              }
            })
            .catch(err => {
              processQueue(err);
              reject(err);
            })
            .finally(() => {
              isTokenRefreshing = false;
            });
        });
      } else if (errStatus === '500') {
        // Lưu error response vào localStorage để hiển thị trên trang error 500
        // Ưu tiên lấy từ error.response?.data nếu có (backend đã format sẵn)
        const responseData = error.response?.data;

        // Kiểm tra nếu lỗi là "Invalid Token!" thì xóa cookie và redirect đến login
        const errorMessage = responseData?.message || error.message || '';
        if (errorMessage.includes('Invalid Token!')) {
          // Xóa cookie access_token (hàm removeFromCookies sẽ tự động thêm prefix RHC_)
          removeFromCookies('access_token');
          // Xóa cookie RHC_access_token trực tiếp (không có prefix) nếu có
          document.cookie = 'RHC_access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; samesite=lax;';
          // Xóa localStorage liên quan đến auth
          const { removeFromLocalStorage } = await import('./utils');
          removeFromLocalStorage(STORAGE_KEYS.USER_ID);
          removeFromLocalStorage(STORAGE_KEYS.USER_NAME);
          removeFromLocalStorage(STORAGE_KEYS.IS_AUTH);
          // Clear Redux store
          dispatch(removeUserInfo());
          // Redirect đến trang login
          navigateToLoginWithCurrentUrl();
          return Promise.reject(error);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let errorResponse: any;

        if (responseData?.input) {
          // Backend đã trả về đầy đủ thông tin trong input
          // Filter attributes để loại bỏ thông tin nhạy cảm về cấu trúc code
          const filteredAttributes: Record<string, unknown> = {};
          if (responseData.input.attributes) {
            Object.keys(responseData.input.attributes).forEach(key => {
              // Chỉ giữ lại các trường không lộ thông tin về code structure
              if (
                key.includes('uriTemplateVariables') ||
                key.includes('pathWithinHandlerMapping') ||
                key.includes('bestMatchingPattern')
              ) {
                filteredAttributes[key] = responseData.input.attributes[key];
              }
              // Loại bỏ bestMatchingHandler vì chứa tên class và method signature
            });
          }

          errorResponse = {
            message: responseData.message || error.message || 'Internal Server Error',
            messageDetail: responseData.messageDetail || null,
            input: {
              headers: responseData.input.headers || error.config?.headers || {},
              pathVariables: responseData.input.pathVariables || {},
              method: responseData.input.method || error.config?.method?.toUpperCase() || 'GET',
              queryParams: responseData.input.queryParams || {},
              queryString: responseData.input.queryString,
              uri: responseData.input.uri,
              url: responseData.input.url || error.config?.url || error.request?.responseURL || '',
              attributes: Object.keys(filteredAttributes).length > 0 ? filteredAttributes : undefined,
            },
            timestamp: responseData.timestamp || new Date().toISOString(),
          };
        } else {
          // Nếu backend chưa trả về input, tự extract từ error
          const url = error.config?.url || error.request?.responseURL || '';
          let fullUrl = url;
          const pathVariables: Record<string, string> = {};
          let queryParams: Record<string, unknown> = {};

          try {
            fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
            const urlObj = new URL(fullUrl);

            // Extract path variables từ URL
            const pathParts = urlObj.pathname.split('/').filter(Boolean);
            pathParts.forEach((part) => {
              if (/^\d+$/.test(part)) {
                const lastPart = pathParts[pathParts.length - 1];
                if (part === lastPart) {
                  pathVariables['id'] = part;
                } else {
                  pathVariables[`param${Object.keys(pathVariables).length}`] = part;
                }
              }
            });

            // Extract query params
            queryParams = Object.fromEntries(urlObj.searchParams) || {};
          } catch (e) {
            // Nếu không parse được URL, dùng giá trị từ config
            queryParams = error.config?.params || {};
          }

          errorResponse = {
            message: responseData?.message || error.message || 'Internal Server Error',
            messageDetail: responseData?.messageDetail || null,
            input: {
              headers: error.config?.headers || {},
              pathVariables: pathVariables,
              method: error.config?.method?.toUpperCase() || 'GET',
              queryParams: queryParams,
              url: fullUrl,
            },
            timestamp: responseData?.timestamp || new Date().toISOString(),
          };
        }

        const { saveToLocalStorage } = await import('./utils');
        saveToLocalStorage(STORAGE_KEYS.ERROR_500_RESPONSE, errorResponse);
        navigate(PATH['ERROR_500_PAGE']);
      }

      return Promise.reject(error);
    }
  );

  return <>{props.children}</>;
};
